/*
  BOX IMPORT — Envío por Correo Argentino en el carrito
  Archivo: assets/box-import-envio.js (lo usa snippets/bi-envio-correo.liquid)
  En 3 pasos: el cliente dice dónde está (o usa su ubicación), elige domicilio o sucursal y, si es sucursal, cuál:
    - el precio del envío a domicilio,
    - el precio del retiro en sucursal y las 3 sucursales de Correo Argentino más cercanas.
  La sucursal elegida se guarda en el pedido como atributo del carrito ("Sucursal Correo Argentino").
  Datos: sucursales del buscador público de Correo Argentino (archivo en Contenido → Archivos),
  ubicación de la dirección con la API Georef del Estado (datos.gob.ar) y, para códigos postales, OpenStreetMap.
  Las tarifas son las mismas cargadas en Configuración → Envío (perfil general).
*/
(() => {
  if (customElements.get('bi-envio-correo')) return;

  const DATOS = 'https://cdn.shopify.com/s/files/1/0845/6362/4189/files/bi-correo-sucursales.json?v=1791757705';
  const GEOREF = 'https://apis.datos.gob.ar/georef/api';
  const CLAVE = 'bi-envio-correo';
  const ATRIBUTO = 'Sucursal Correo Argentino';

  // Código INDEC de Georef → letra de provincia de Shopify/Correo
  const INDEC = { '02': 'C', '06': 'B', '10': 'K', '14': 'X', '18': 'W', '22': 'H', '26': 'U', '30': 'E', '34': 'P', '38': 'Y', '42': 'L', '46': 'F', '50': 'M', '54': 'N', '58': 'Q', '62': 'R', '66': 'A', '70': 'J', '74': 'D', '78': 'Z', '82': 'S', '86': 'G', '90': 'T', '94': 'V' };
  const LETRA_A_INDEC = Object.fromEntries(Object.entries(INDEC).map(([k, v]) => [v, k]));

  // Tarifas por zona: [domicilio ≤1 kg, 1,01–5 kg, >5 kg], [sucursal ≤1 kg, 1,01–5 kg, >5 kg], días hábiles
  const ZONAS = [
    { provincias: 'C', dom: [8046, 12020, 14957], suc: [5428, 7920, 11491], dias: '2 a 5' },
    { provincias: 'BXSEL', dom: [9648, 15957, 21342], suc: [6739, 14050, 21342], dias: '3 a 8' },
    { provincias: 'MJDTGKFHWNPQR', dom: [10509, 17880, 25423], suc: [7415, 16927, 25423], dias: '5 a 10' },
    { provincias: 'AYUZV', dom: [10586, 19724, 30568], suc: [7745, 19724, 30568], dias: '7 a 12' },
  ];

  const dinero = (n) => '$' + Math.round(n).toLocaleString('es-AR');
  const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const titulo = (s) => s.toLowerCase().replace(/(^|[\s(/.-])([a-záéíóúñü])/g, (m, a, b) => a + b.toUpperCase()).replace(/\bN°/gi, 'N°');
  const km = (a, b, c, d) => {
    const r = Math.PI / 180, x = Math.sin(((c - a) * r) / 2), y = Math.sin(((d - b) * r) / 2);
    return 12742 * Math.asin(Math.sqrt(x * x + Math.cos(a * r) * Math.cos(c * r) * y * y));
  };
  const leer = () => { try { return JSON.parse(sessionStorage.getItem(CLAVE)) || null; } catch (e) { return null; } };
  const guardar = (v) => { try { sessionStorage.setItem(CLAVE, JSON.stringify(v)); } catch (e) {} };

  let sucursales = null;
  const cargarSucursales = () => (sucursales ||= fetch(DATOS).then((r) => { if (!r.ok) throw new Error('datos'); return r.json(); }).catch((e) => { sucursales = null; throw e; }));

  const getJSON = async (url) => { const r = await fetch(url); if (!r.ok) throw new Error('red'); return r.json(); };

  // Devuelve { lat, lon, prov, texto } o null
  async function ubicar(texto, prov) {
    const t = texto.trim();
    const pIndec = LETRA_A_INDEC[prov];
    if (/^[a-z]?\d{4}[a-z]{0,3}$/i.test(t.replace(/\s/g, ''))) {
      const cp = t.replace(/\D/g, '').slice(0, 4);
      const j = await getJSON(`https://nominatim.openstreetmap.org/search?postalcode=${cp}&country=ar&format=json&limit=1&accept-language=es`);
      if (j[0]) return { lat: +j[0].lat, lon: +j[0].lon, prov, texto: 'CP ' + cp };
      return null;
    }
    if (/\d/.test(t)) {
      const partes = t.split(',');
      const calle = partes.shift();
      const loc = partes.join(' ').trim();
      const q = new URLSearchParams({ direccion: calle, provincia: pIndec, max: '1' });
      if (loc) q.set('localidad', loc);
      let j = await getJSON(`${GEOREF}/direcciones?${q}`);
      if (!j.direcciones?.length && loc) { q.delete('localidad'); q.set('departamento', loc); j = await getJSON(`${GEOREF}/direcciones?${q}`).catch(() => ({})); }
      if (!j.direcciones?.length && loc) { q.delete('departamento'); j = await getJSON(`${GEOREF}/direcciones?${q}`); }
      const d = j.direcciones?.[0];
      if (d?.ubicacion?.lat) return { lat: d.ubicacion.lat, lon: d.ubicacion.lon, prov, texto: titulo(d.nomenclatura) };
      if (!loc) return null;
      return ubicar(loc, prov);
    }
    const q = new URLSearchParams({ nombre: t, provincia: pIndec, max: '1', campos: 'nombre,centroide' });
    let j = await getJSON(`${GEOREF}/localidades?${q}`);
    let l = j.localidades?.[0];
    if (!l) { j = await getJSON(`${GEOREF}/departamentos?${q}`); l = j.departamentos?.[0]; }
    if (l?.centroide) return { lat: l.centroide.lat, lon: l.centroide.lon, prov, texto: l.nombre };
    return null;
  }

  const PROVINCIAS = { C: 'CABA', B: 'Buenos Aires', K: 'Catamarca', H: 'Chaco', U: 'Chubut', X: 'Córdoba', W: 'Corrientes', E: 'Entre Ríos', P: 'Formosa', Y: 'Jujuy', L: 'La Pampa', F: 'La Rioja', M: 'Mendoza', N: 'Misiones', Q: 'Neuquén', R: 'Río Negro', A: 'Salta', J: 'San Juan', D: 'San Luis', Z: 'Santa Cruz', S: 'Santa Fe', G: 'Santiago del Estero', V: 'Tierra del Fuego', T: 'Tucumán' };
  const distancia = (d) => (d < 1 ? Math.round(d * 1000) + ' m' : d.toFixed(1).replace('.', ',') + ' km');
  const ICONO_CASA = '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 11l9-7 9 7"/><path d="M5 10v10h14V10"/><path d="M10 20v-6h4v6"/></svg>';
  const ICONO_SUC = '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 21s-7-6.2-7-11.5A7 7 0 0 1 19 9.5C19 14.8 12 21 12 21z"/><circle cx="12" cy="9.5" r="2.5"/></svg>';

  /*
    Pasos: 1) zona (provincia + CP, localidad o dirección)  2) cómo lo recibe (domicilio o sucursal)  3) qué sucursal.
    Estado en sessionStorage: { lat, lon, prov, texto, consulta, modo: 'domicilio'|'sucursal'|null, suc: 'nombre|dirección'|null, ver: n }
  */
  class BiEnvioCorreo extends HTMLElement {
    connectedCallback() {
      if (this.dataset.listo) return;
      this.dataset.listo = '1';
      this.peso = Number(this.dataset.peso || 0);
      this.campoProv = this.querySelector('[name=provincia]');
      this.campoDir = this.querySelector('[name=direccion]');
      this.zona = this.querySelector('[data-zona]');
      this.res = this.querySelector('[data-resultado]');
      this.msg = this.querySelector('[data-mensaje]');
      this.querySelector('[data-buscar]').addEventListener('click', () => this.buscar());
      this.campoDir.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); this.buscar(); } });
      this.querySelector('[data-mi-ubicacion]')?.addEventListener('click', () => this.miUbicacion());
      this.res.addEventListener('change', (e) => {
        e.stopPropagation();
        if (e.target.name === 'bi-modo') this.elegirModo(e.target.value);
        if (e.target.name === 'bi-suc') this.elegirSucursal(Number(e.target.value));
      });
      this.res.addEventListener('click', (e) => {
        const b = e.target.closest('[data-accion]');
        if (!b) return;
        if (b.dataset.accion === 'cambiar-zona') this.cambiarZona();
        if (b.dataset.accion === 'mas') { const st = leer() || {}; st.ver = (st.ver || 5) + 4; guardar(st); this.pintar(); }
      });
      const previo = leer();
      // Si el pedido dice que ya eligió pero este navegador no tiene la elección completa (otra pestaña, otro navegador
      // como el de Instagram), se pide elegir de nuevo: así al pagar siempre se carga la sucursal o la zona.
      const completo = previo?.modo === 'domicilio' || (previo?.modo === 'sucursal' && previo?.sucDatos);
      if (this.dataset.elegido && !completo) delete this.dataset.elegido;
      if (previo?.lat) {
        this.campoDir.value = previo.consulta || '';
        if (previo.prov) this.campoProv.value = previo.prov;
        this.pintar();
      }
    }

    aviso(t, tipo = '') { this.msg.textContent = t; this.msg.dataset.tipo = tipo; }

    async buscar() {
      const texto = this.campoDir.value;
      const prov = this.campoProv.value;
      if (!prov) { this.aviso('Elegí tu provincia.', 'error'); this.campoProv.focus(); return; }
      if (texto.trim().length < 3) { this.aviso('Escribí tu código postal o tu localidad.', 'error'); this.campoDir.focus(); return; }
      this.aviso('Buscando…');
      this.toggleAttribute('cargando', true);
      try {
        const [lugar] = await Promise.all([ubicar(texto, prov), cargarSucursales()]);
        if (!lugar) { this.aviso('No lo encontramos. Probá solo con tu localidad (ej: Martínez) o tu código postal (ej: 1640).', 'error'); return; }
        guardar({ ...lugar, consulta: texto, modo: null, suc: null, ver: 5 });
        this.marcar('', '');
        await this.pintar();
      } catch (e) {
        this.aviso('No pudimos buscar ahora. Revisá tu conexión e intentá de nuevo.', 'error');
      } finally { this.removeAttribute('cargando'); }
    }

    miUbicacion() {
      if (!navigator.geolocation) { this.aviso('Tu navegador no permite usar la ubicación. Escribí tu código postal o localidad.', 'error'); return; }
      this.aviso('Buscando tu ubicación…');
      this.toggleAttribute('cargando', true);
      navigator.geolocation.getCurrentPosition(async (pos) => {
        try {
          const { latitude: lat, longitude: lon } = pos.coords;
          const [j] = await Promise.all([getJSON(`${GEOREF}/ubicacion?lat=${lat}&lon=${lon}`), cargarSucursales()]);
          const prov = INDEC[j.ubicacion?.provincia?.id];
          if (!prov) { this.aviso('No pudimos ubicarte en Argentina. Escribí tu código postal o localidad.', 'error'); return; }
          this.campoProv.value = prov;
          const lugar = j.ubicacion.municipio?.nombre || j.ubicacion.departamento?.nombre || '';
          this.campoDir.value = lugar;
          guardar({ lat, lon, prov, texto: lugar || 'tu ubicación', consulta: lugar, modo: null, suc: null, ver: 5 });
          this.marcar('', '');
          await this.pintar();
        } catch (e) { this.aviso('No pudimos usar tu ubicación. Escribí tu código postal o localidad.', 'error'); }
        finally { this.removeAttribute('cargando'); }
      }, () => { this.removeAttribute('cargando'); this.aviso('No diste permiso para usar tu ubicación. Escribí tu código postal o localidad.', 'error'); }, { timeout: 12000, maximumAge: 600000 });
    }

    cambiarZona() {
      this.zona.hidden = false;
      this.res.innerHTML = '';
      this.campoDir.focus();
    }

    async pintar() {
      const st = leer();
      if (!st?.lat) return;
      const datos = await cargarSucursales();
      const zona = ZONAS.find((z) => z.provincias.includes(st.prov)) || ZONAS[1];
      const banda = this.peso <= 1000 ? 0 : this.peso <= 5000 ? 1 : 2;
      const pDom = zona.dom[banda], pSuc = zona.suc[banda];
      let lista = (datos[st.prov] || []).map((s) => ({ s, d: km(st.lat, st.lon, s[4], s[5]) }));
      if (st.prov === 'C' || st.prov === 'B') lista = lista.concat((datos[st.prov === 'C' ? 'B' : 'C'] || []).map((s) => ({ s, d: km(st.lat, st.lon, s[4], s[5]) })));
      lista.sort((a, b) => a.d - b.d);
      this.cerca = lista.slice(0, Math.min(st.ver || 5, 12));
      const id = (s) => `${s[0]}|${s[1]}`;
      const lugar = `${esc(st.texto)}${st.texto.includes(PROVINCIAS[st.prov]) ? '' : ', ' + PROVINCIAS[st.prov]}`;

      const sucursales = this.cerca.map(({ s, d }, i) => `
        <label class="bi-envio__suc">
          <input type="radio" name="bi-suc" value="${i}" ${st.suc === id(s) ? 'checked' : ''}>
          <span class="bi-envio__suc-txt">
            <b>${esc(titulo(s[0]))}${s[6] === 'S' ? '' : ' <small>punto Correo</small>'}</b>
            <span>${esc(titulo(s[1]))}, ${esc(titulo(s[2]))}</span>
            ${s[3] ? `<span class="bi-envio__hora">${esc(s[3].toLowerCase())}</span>` : ''}
          </span>
          <span class="bi-envio__km">${distancia(d)}<a href="https://www.google.com/maps/search/?api=1&query=${s[4]},${s[5]}" target="_blank" rel="noopener">Mapa</a></span>
        </label>`).join('');

      const elegida = st.modo === 'sucursal' && st.suc ? this.cerca.find(({ s }) => id(s) === st.suc)?.s : null;
      let listo = '';
      if (st.modo === 'domicilio') listo = `<b>Listo: te lo llevamos a tu casa.</b> En el pago completá tu dirección y elegí <b>“Correo Argentino a domicilio”</b>.`;
      else if (elegida) listo = `<b>Listo: lo retirás en ${esc(titulo(elegida[0]))}</b> (${esc(titulo(elegida[1]))}, ${esc(titulo(elegida[2]))}).<br>Al tocar “Pagar” la dirección de esta sucursal ya aparece cargada como lugar de entrega: completá tu nombre y celular, y elegí <b>“Correo Argentino retiro en la sucursal”</b>.`;

      this.zona.hidden = true;
      this.querySelector('[data-guardada]')?.setAttribute('hidden', '');
      this.res.innerHTML = `
        <div class="bi-envio__lugar">
          <span>Enviamos a <b>${lugar}</b></span>
          <button type="button" class="bi-envio__link" data-accion="cambiar-zona">Cambiar</button>
        </div>
        <p class="bi-envio__paso"><span>2</span> ¿Cómo lo querés recibir?</p>
        <div class="bi-envio__modos" role="radiogroup">
          <label class="bi-envio__modo">
            <input type="radio" name="bi-modo" value="domicilio" ${st.modo === 'domicilio' ? 'checked' : ''}>
            <span class="bi-envio__modo-ic">${ICONO_CASA}</span>
            <span class="bi-envio__modo-tit">A domicilio</span>
            <span class="bi-envio__modo-precio">${dinero(pDom)}</span>
            <span class="bi-envio__modo-sub">${zona.dias} días hábiles</span>
          </label>
          <label class="bi-envio__modo">
            <input type="radio" name="bi-modo" value="sucursal" ${st.modo === 'sucursal' ? 'checked' : ''}>
            <span class="bi-envio__modo-ic">${ICONO_SUC}</span>
            <span class="bi-envio__modo-tit">Retiro en sucursal</span>
            <span class="bi-envio__modo-precio">${dinero(pSuc)}</span>
            <span class="bi-envio__modo-sub">${pDom > pSuc ? `Ahorrás ${dinero(pDom - pSuc)}` : `${zona.dias} días hábiles`}</span>
          </label>
        </div>
        ${st.modo !== 'domicilio' ? `
          <p class="bi-envio__paso"><span>3</span> ${st.modo === 'sucursal' ? 'Elegí tu sucursal de Correo Argentino' : 'Sucursales de Correo Argentino cerca tuyo: tocá una para retirar ahí'}</p>
          <div class="bi-envio__sucs" role="radiogroup">${sucursales || '<p class="bi-envio__vacio">No encontramos sucursales cerca. Probá con otra localidad.</p>'}</div>
          ${lista.length > this.cerca.length && this.cerca.length < 12 ? '<button type="button" class="bi-envio__link bi-envio__mas" data-accion="mas">Ver más sucursales</button>' : ''}` : ''}
        ${listo ? `<p class="bi-envio__listo">${listo}</p>` : ''}`;
      this.aviso('');
      this.toggleAttribute('elegido-ok', !!listo);
    }

    async elegirModo(modo) {
      const st = leer() || {};
      st.modo = modo;
      if (modo === 'domicilio') st.suc = null;
      guardar(st);
      await this.pintar();
      if (modo === 'domicilio') await this.marcar('', 'A domicilio');
      else {
        await this.marcar('', '');
        this.res.querySelector('.bi-envio__sucs')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }

    async elegirSucursal(i) {
      const s = this.cerca?.[i]?.s;
      if (!s) return;
      const st = leer() || {};
      st.modo = 'sucursal';
      st.suc = `${s[0]}|${s[1]}`;
      st.sucDatos = { nombre: titulo(s[0]), dir: titulo(s[1]), loc: titulo(s[2]), cp: '' };
      guardar(st);
      await this.pintar();
      // Código postal de la sucursal, para cargarla completa en el pago (si no se encuentra, el cliente lo completa)
      getJSON(`https://nominatim.openstreetmap.org/reverse?lat=${s[4]}&lon=${s[5]}&format=json&zoom=18&accept-language=es`)
        .then((j) => {
          const cp = String(j?.address?.postcode || '').replace(/\D/g, '').slice(0, 4);
          const ahora = leer();
          if (cp.length === 4 && ahora?.suc === st.suc) { ahora.sucDatos.cp = cp; guardar(ahora); }
        }).catch(() => {});
      await this.marcar(`${titulo(s[0])} — ${titulo(s[1])}, ${titulo(s[2])} (${PROVINCIAS[st.prov] || st.prov})`, 'Retiro en sucursal');
    }

    // Guarda la elección en el pedido (atributos del carrito) y marca si ya se puede pagar.
    async marcar(sucursal, tipo) {
      if (tipo) { this.dataset.elegido = '1'; this.removeAttribute('falta'); } else delete this.dataset.elegido;
      try {
        await fetch((window.Shopify?.routes?.root || '/') + 'cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes: { [ATRIBUTO]: sucursal, 'Envío elegido en el carrito': tipo } }),
        });
      } catch (e) {
        this.aviso('No pudimos guardar tu elección. Probá de nuevo.', 'error');
      }
    }
  }

  customElements.define('bi-envio-correo', BiEnvioCorreo);

  // Antes de ir al pago, el cliente elige domicilio o una sucursal: así el pedido dice a cuál mandarlo.
  document.addEventListener('click', (e) => {
    const boton = e.target instanceof Element && e.target.closest('button[name="checkout"], input[name="checkout"], a[href="/checkout"], a[href^="/checkout?"]');
    if (!boton) return;
    const caja = [...document.querySelectorAll('bi-envio-correo')].find((el) => el.offsetParent !== null);
    if (!caja) return;
    if (caja.dataset.elegido) {
      // Si hay productos de Pagá en la Puerta, su propio formulario carga la dirección del domicilio: no lo pisamos
      if ([...document.querySelectorAll('[data-bi-dir]:not([data-modo="consulta"])')].some((b) => b.offsetParent !== null)) return;
      const st = leer();
      if (!st) return;
      const a = 'checkout[shipping_address]';
      const q = [a + '[country]=AR'];
      const poner = (k, v) => { if (v) q.push(`${a}[${k}]=${encodeURIComponent(v)}`); };
      const cpCliente = /^\D*\d{4}\D*$/.test(st.consulta || '') ? String(st.consulta).replace(/\D/g, '') : '';
      if (st.modo === 'sucursal' && st.sucDatos) {
        // Retiro en sucursal: la entrega va a la sucursal elegida, así el cliente la ve en el pago y nosotros en el pedido
        poner('province', st.prov);
        poner('address1', st.sucDatos.dir);
        poner('address2', `Retira en sucursal Correo Argentino: ${st.sucDatos.nombre}`);
        poner('city', st.sucDatos.loc);
        poner('zip', st.sucDatos.cp || cpCliente);
      } else {
        poner('province', st.prov);
        poner('zip', cpCliente);
      }
      e.preventDefault();
      e.stopImmediatePropagation();
      window.location.href = '/checkout?' + q.join('&');
      return;
    }
    e.preventDefault();
    e.stopImmediatePropagation();
    caja.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const st = leer();
    const falta = !st?.lat ? 'Antes de pagar, contanos dónde estás y elegí cómo lo recibís.'
      : st.modo === 'sucursal' ? 'Elegí en qué sucursal lo retirás.'
      : 'Antes de pagar, elegí si lo querés a domicilio o retirarlo en una sucursal.';
    caja.aviso(falta, 'error');
    caja.toggleAttribute('falta', true);
    (caja.querySelector('[data-resultado] input') || caja.campoProv)?.focus({ preventScroll: true });
  }, true);
})();
